interface MetricVersion {
  id?: number
  unit?: string
  unit_descriptor?: string
  year?: string
  is_default?: string
  value?: number
  disaggregations?: any[]
}

interface Metric {
  id?: number
  name?: string
  category?: string
  versions?: MetricVersion[]
  categoryDescription?: string
}

interface DashboardCardProps {
  category: string
  metrics: Metric[]
  description: string
  colorAccent?: string
}

function formatValue(value: any, unit: any): string {
  // Handle undefined or null values
  if (value === undefined || value === null) {
    return "N/A"
  }

  // Convert value to number if it's a string
  const numValue = typeof value === "string" ? Number.parseFloat(value) : value

  // Check if conversion resulted in a valid number
  if (typeof numValue !== "number" || isNaN(numValue)) {
    return String(value)
  }

  // Format based on unit (with null/undefined check)
  if (unit === "percentage") {
    return `${Math.round(numValue * 100)}%`
  } else if (unit === "currency") {
    if (numValue >= 1000000) {
      return `$${(numValue / 1000000).toFixed(2)}M`
    } else if (numValue >= 100000) {
      return `$${Math.round(numValue / 1000)}K`
    } else {
      return `$${Math.round(numValue).toLocaleString()}`
    }
  } else if (unit === "integer") {
    if (numValue >= 1000000) {
      return `${(numValue / 1000000).toFixed(2)}M`
    } else if (numValue >= 1000) {
      return `${(numValue / 1000).toFixed(1)}K`
    } else {
      // Round to 2 decimal places for smaller integers too
      return numValue.toFixed(2)
    }
  } else if (unit === "decimal") {
    // Round to 2 decimal places for decimal unit type
    return numValue.toFixed(2)
  }

  // Default case
  return numValue.toString()
}

// Function to calculate the width percentage for the bar
// function calculateBarWidth(value: number | undefined, unit: string | undefined): string {
//   if (value === undefined) return "0%"

//   // For percentages, scale directly (0-100%)
//   if (unit === "percentage") {
//     const percentage = Math.min(Math.max(value * 100, 0), 100)
//     return `${percentage}%`
//   }
//   // For integer and decimal, use a fixed width
//   else if (unit === "integer" || unit === "decimal" || unit === "currency") {
//     return "0.75rem" // Fixed width for integer and decimal
//   }
//   // For other types, normalize to a 0-200 scale
//   else {
//     // Assume a reasonable max value of 200 for non-percentage values
//     const normalizedValue = Math.min(Math.max(value, 0), 200)
//     return `${(normalizedValue / 200) * 100}%`
//   }
// }

// Function to get a color based on category name
function getCategoryColor(category: string): string {
  const colors = {
    Economy: "bg-orange-50 border-orange-200 text-orange-700",
    Education: "bg-emerald-50 border-emerald-200 text-emerald-700",
    Health: "bg-rose-50 border-rose-200 text-rose-700",
    Housing: "bg-amber-50 border-amber-200 text-amber-700",
    Demographics: "bg-blue-50 border-blue-200 text-blue-700",
    Demographic: "bg-blue-50 border-blue-200 text-blue-700",
    Environment: "bg-teal-50 border-teal-200 text-teal-700",
    Transportation: "bg-indigo-50 border-indigo-200 text-indigo-700",
    Workforce: "bg-red-50 border-red-200 text-red-700",
    Livability: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700",
    Accessibility: "bg-purple-50 border-purple-200 text-purple-700",
    default: "bg-slate-50 border-slate-200 text-slate-700",
  }

  return colors[category as keyof typeof colors] || colors.default
}

// Function to get bar color based on category
// function getBarColor(category: string): string {
//   const colors = {
//     Economy: "bg-orange-500",
//     Education: "bg-emerald-500",
//     Health: "bg-rose-500",
//     Housing: "bg-amber-500",
//     Demographics: "bg-blue-500",
//     Demographic: "bg-blue-500",
//     Environment: "bg-teal-500",
//     Transportation: "bg-indigo-500",
//     Workforce: "bg-red-500",
//     Livability: "bg-fuchsia-500",
//     Accessibility: "bg-purple-500",
//     default: "bg-slate-500",
//   }

//   return colors[category as keyof typeof colors] || colors.default
// }

export function DashboardCard({ category, metrics, description }: DashboardCardProps) {
  const categoryColor = getCategoryColor(category);
//   const barColor = getBarColor(category);

    return (
    <div className="w-full overflow-hidden border border-slate-200 shadow-sm transition-all duration-200 hover:shadow-md">
        <div className={`${categoryColor} border-b px-4 py-2`}>
            <div className="font-bold">{category}</div>
        </div>
        <div>
            <div className="dashboard-metrics-grid grid grid-cols-1 md:grid-cols-5 gap-1 px-3">
            {metrics.map((metric, index) => {
                // Get versions or empty array if missing
                const versions = metric.versions || []

                // Sort versions by year descending and take the most recent one
                const sortedVersions = [...versions].sort((a, b) => {
                const yearA = a.year ? Number.parseInt(a.year) : 0
                const yearB = b.year ? Number.parseInt(b.year) : 0
                return yearB - yearA
                })

                return (
                <div key={metric.id || `metric-${index}`} className="p-2 flex flex-col">
                    <p className="text-xs font-medium mb-1">{metric.name || "Unnamed Metric"}</p>

                    {sortedVersions.length > 0 ? (
                    sortedVersions.map((version, vIndex) => (
                        <div key={vIndex} className="flex items-center mb-1 last:mb-0">
                            <div className="flex items-end flex-grow h-3 mr-1">
                                <span className="font-medium text-slate-500 whitespace-nowrap text-[10px] min-w-8">
                                {version.year}
                                </span>
                                {/* <div
                                className={`h-3 ${barColor}`}
                                style={{
                                    width: calculateBarWidth(version.value, version.unit),
                                    borderRadius: 0,
                                }}
                                ></div> */}
                                <span className="font-bold whitespace-nowrap text-xs ml-1">
                                {formatValue(version.value, version.unit)}
                                </span>
                            </div>
                        </div>
                    ))
                    ) : (
                    <div className="flex items-center">
                        <div className="flex-grow h-3 mr-2"></div>
                        <span className="text-xs font-bold text-slate-700">N/A</span>
                    </div>
                    )}
                </div>
                )
            })}
            </div>
            <div className="bg-slate-50 p-2 border-t border-slate-100 flex justify-between items-center">
                <div className="text-xs text-slate-500 italic">{description}</div>
            </div>
        </div>
    </div>
    )
}